import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import FormularioButom from "./FormularioButom";
import FormularioInput from "./FormularioInput";
import FormularioList from "./FormularioList";
import "./Formulario.css";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import {
  ACCESS_TYPES,
  getCreatableAccessTypes,
  isSuperAdmin,
  normalizeAccessType,
} from "../../utils/accessControl";

const onlyDigits = (v = "") => String(v).replace(/\D/g, "");
const SMARTOLFATIVOS_DOMAIN = "@smartolfativos.com.br";

const isRealgemsAccessType = (value) => {
  const normalized = normalizeAccessType(value);
  return (
    normalized === ACCESS_TYPES.REALGEMS_USER ||
    normalized === ACCESS_TYPES.REALGEMS_ADMIN
  );
};

const parseMoneyBR = (value) => {
  if (value === null || value === undefined) return 0;

  const str = String(value).trim();
  if (!str) return 0;

  const cleaned = str.replace(/[^\d.,-]/g, "");

  if (cleaned.includes(",")) {
    const normalized = cleaned.replace(/\./g, "").replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatMoneyBR = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0,00";
  return v.toFixed(2).replace(".", ",");
};

const isCpfOrCnpjValid = (value) => {
  const v = onlyDigits(value);
  return v.length === 11 || v.length === 14;
};

const buildSchema = (isEdit, allowedAccessTypes) =>
  Yup.object({
    nome: Yup.string()
      .trim()
      .min(3, "Informe pelo menos 3 caracteres.")
      .required("Nome / Razao social e obrigatorio."),
    acesso: Yup.mixed()
      .oneOf(allowedAccessTypes, "Selecione um acesso valido.")
      .required("Acesso e obrigatorio."),
    email: Yup.string()
      .trim()
      .email("E-mail invalido.")
      .required("E-mail e obrigatorio.")
      .test("email-access-domain", function validateEmailDomain(value) {
        if (!value) return true;

        const selectedAccessType = this.parent?.acesso;
        if (isRealgemsAccessType(selectedAccessType)) {
          return true;
        }

        if (value.toLowerCase().endsWith(SMARTOLFATIVOS_DOMAIN)) {
          return true;
        }

        return this.createError({
          message: `O e-mail deve ser do dominio ${SMARTOLFATIVOS_DOMAIN}`,
        });
      }),
    cpfCnpj: Yup.string()
      .required("CPF ou CNPJ e obrigatorio.")
      .test("cpf-cnpj", "Informe um CPF (11) ou CNPJ (14) valido.", isCpfOrCnpjValid),
    telefone: Yup.string()
      .required("Telefone e obrigatorio.")
      .test("phone", "Telefone deve ter DDD + numero (10 ou 11 digitos).", (v) => {
        const d = onlyDigits(v);
        return d.length === 10 || d.length === 11;
      }),
    status: Yup.mixed()
      .oneOf(["ACTIVE", "INACTIVE"], "Selecione um status valido.")
      .required("Status e obrigatorio."),
    senha: isEdit
      ? Yup.string()
          .transform((v) => (v ? v : ""))
          .test("min-if-filled", "A senha deve ter no minimo 6 caracteres.", (v) => {
            if (!v) return true;
            return v.length >= 6;
          })
      : Yup.string()
          .required("Senha e obrigatoria.")
          .min(6, "A senha deve ter no minimo 6 caracteres."),
    withdrawLimit: Yup.number()
      .typeError("Informe um numero valido para o limite.")
      .min(0, "O limite nao pode ser negativo.")
      .required("Limite e obrigatorio."),
    omieVendorCode: Yup.string().test(
      "omie-vendor-code",
      "Informe apenas numeros no codigo do vendedor Omie.",
      (value) => !value || /^\d+$/.test(String(value).trim())
    ),
  });

const FormularioAddUsuarios = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [telefone, setTelefone] = useState("");
  const [acesso, setAcesso] = useState("");
  const [status, setStatus] = useState("");
  const [senha, setSenha] = useState("");
  const [withdrawLimit, setWithdrawLimit] = useState("0,00");
  const [omieVendorCode, setOmieVendorCode] = useState("");

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const viewerRole = normalizeAccessType(user?.access_type);
  const viewerIsSuperAdmin = isSuperAdmin(viewerRole);
  const listaAcesso = useMemo(() => getCreatableAccessTypes(viewerRole), [viewerRole]);
  const accessValues = useMemo(() => listaAcesso.map((item) => item.value), [listaAcesso]);
  const schema = useMemo(() => buildSchema(isEdit, accessValues), [isEdit, accessValues]);
  const isRealgemsAccessSelected = useMemo(() => isRealgemsAccessType(acesso), [acesso]);

  useEffect(() => {
    if (!isEdit && !acesso && accessValues.length === 1) {
      setAcesso(accessValues[0]);
    }
  }, [acesso, accessValues, isEdit]);

  useEffect(() => {
    const carregar = async () => {
      if (!isEdit) return;

      try {
        setLoading(true);
        setErrors({});

        const res = await Api.get(`/usuarios/${id}`);
        const u = res.data;

        setNome(u.full_name ?? "");
        setEmail(u.email ?? "");
        setCpfCnpj(u.cpf_cnpj ?? "");
        setTelefone(u.phone ?? "");
        setAcesso(u.access_type ?? "");
        setStatus(u.status ?? "");
        setWithdrawLimit(formatMoneyBR(u.withdraw_limit ?? 0));
        setOmieVendorCode(u.omie_vendor_code ?? "");
        setSenha("");
      } catch (err) {
        const message =
          err?.response?.data?.message || "Nao foi possivel carregar o usuario solicitado.";
        alert(message);
        navigate("/usuarios");
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [id, isEdit, navigate]);

  const aoSalvar = async (evento) => {
    evento.preventDefault();

    try {
      setErrors({});
      await schema.validate(
        {
          nome,
          email,
          cpfCnpj,
          telefone,
          acesso,
          status,
          senha,
          withdrawLimit: parseMoneyBR(withdrawLimit),
          omieVendorCode,
        },
        { abortEarly: false }
      );
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const fieldErrors = {};
        err.inner.forEach((e) => {
          if (e.path) fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        full_name: nome.trim(),
        email: email.trim(),
        cpf_cnpj: onlyDigits(cpfCnpj),
        phone: onlyDigits(telefone),
        access_type: acesso,
        status,
        withdraw_limit: parseMoneyBR(withdrawLimit),
        omie_vendor_code: omieVendorCode.trim(),
      };

      if (!isEdit || (isEdit && senha.trim())) {
        payload.password_hash = senha;
      }

      if (isEdit) {
        await Api.put(`/usuarios/${id}`, payload);
      } else {
        await Api.post("/cadastrousuarios", payload);
      }

      navigate("/usuarios");
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.response?.data?.msg || "";
      const apiField = err?.response?.data?.field || "";

      if (apiField === "omieVendorCode") {
        setErrors((prev) => ({
          ...prev,
          omieVendorCode:
            apiMessage || "Codigo vendedor Omie ja vinculado a outro licenciado.",
        }));
      } else if (apiMessage) {
        alert(apiMessage);
      }

      console.log("ERRO ao salvar:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const listaStatus = [
    { label: "Ativo", value: "ACTIVE" },
    { label: "Inativo", value: "INACTIVE" },
  ];

  const cpfCnpjMask = [{ mask: "000.000.000-00" }, { mask: "00.000.000/0000-00" }];
  const cpfCnpjDispatch = (appended, masked) => {
    const next = onlyDigits((masked.value || "") + appended);
    return next.length > 11 ? masked.compiledMasks[1] : masked.compiledMasks[0];
  };

  const accessHelper = viewerIsSuperAdmin
    ? "Super administrador pode criar licenciados, administradores e super administradores."
    : "Administrador comum pode criar e editar apenas usuarios licenciados.";
  const isAccessLocked = accessValues.length <= 1;
  const submitLabel = isEdit
    ? "Salvar alteracoes"
    : acesso === ACCESS_TYPES.LICENSED
    ? "Salvar licenciado"
    : "Salvar usuario";

  return (
    <form id="bank-form" noValidate onSubmit={aoSalvar}>
      {loading && <p>Carregando...</p>}

      <FormularioInput
        type="text"
        size="full"
        title="Nome / Razao social"
        required="required"
        id="nomeLicenciado"
        placeholder="Ex: Joao Silva, Clinica Bem-Estar..."
        helper="Nome que aparecera no extrato e na area do usuario."
        valor={nome}
        aoAlterado={(valor) => setNome(valor)}
        error={errors.nome}
        readOnly={isEdit}
      />

      <FormularioList
        itens={listaAcesso}
        title="Acesso"
        required="required"
        id="acessoLicenciado"
        placeholder="Acesso"
        helper={accessHelper}
        lista="listaAcesso"
        valor={acesso}
        aoAlterado={(valor) => setAcesso(valor)}
        error={errors.acesso}
        disabled={isAccessLocked}
      />

      <FormularioInput
        type="email"
        title="E-mail de acesso"
        required="required"
        id="emailLicenciado"
        placeholder={
          isRealgemsAccessSelected ? "nome@empresa.com.br" : "nome@smartolfativos.com.br"
        }
        helper={
          isRealgemsAccessSelected
            ? "E-mail unico que sera utilizado no login. Para perfis Realgems, aceitamos qualquer dominio valido."
            : "E-mail unico que sera utilizado no login. Para este perfil, use um endereco @smartolfativos.com.br."
        }
        valor={email}
        aoAlterado={(valor) => setEmail(valor)}
        error={errors.email}
      />

      <FormularioInput
        type="text"
        title="CPF ou CNPJ"
        required="required"
        id="cpfCnpj"
        placeholder="000.000.000-00"
        valor={cpfCnpj}
        aoAlterado={(v) => setCpfCnpj(v)}
        error={errors.cpfCnpj}
        helper="Documento utilizado para notas fiscais e pagamentos."
        mask={cpfCnpjMask}
        dispatch={cpfCnpjDispatch}
      />

      <FormularioInput
        type="text"
        title="WhatsApp"
        required="required"
        id="telefoneLicenciado"
        placeholder="(00) 00000-0000"
        valor={telefone}
        aoAlterado={(v) => setTelefone(v)}
        error={errors.telefone}
        helper="Contato direto com o usuario."
        mask="(00) 00000-0000"
      />

      <FormularioList
        itens={listaStatus}
        title="Status"
        required="required"
        id="statusLicenciado"
        placeholder="Status"
        helper="Usuarios inativos nao conseguem acessar o painel."
        lista="listaStatus"
        valor={status}
        aoAlterado={(valor) => setStatus(valor)}
        error={errors.status}
      />

      <FormularioInput
        type="text"
        title="Limite de saque (R$)"
        required="required"
        id="withdrawLimit"
        placeholder="Ex: 500,00"
        helper="Limite de saque permitido para o usuario."
        valor={withdrawLimit}
        aoAlterado={(v) => setWithdrawLimit(v)}
        error={errors.withdrawLimit}
      />

      <FormularioInput
        type="text"
        title="Codigo vendedor Omie"
        id="omieVendorCode"
        placeholder="Ex: 2155134157"
        helper="Quando este codigo vier no pedido da Omie, o licenciado sera preenchido automaticamente."
        valor={omieVendorCode}
        aoAlterado={(valor) => {
          setOmieVendorCode(valor);
          setErrors((prev) => ({ ...prev, omieVendorCode: undefined }));
        }}
        error={errors.omieVendorCode}
      />

      <FormularioInput
        type="password"
        title="Senha"
        required={!isEdit ? "required" : undefined}
        id="senhaLicenciado"
        placeholder={isEdit ? "Deixe em branco para manter a senha atual" : "Defina uma senha temporaria"}
        helper={isEdit ? "Se preencher, a senha sera alterada." : "O usuario podera alterar a senha depois no primeiro acesso."}
        valor={senha}
        aoAlterado={(valor) => setSenha(valor)}
        error={errors.senha}
      />

      <div className="form-footer">
        <FormularioButom icone="💾" disabled={loading}>
          {submitLabel}
        </FormularioButom>
      </div>
    </form>
  );
};

export default FormularioAddUsuarios;
